import Permissions from '@app/src/auth/permissions'
import { usePageBreadcrumbs } from '@app/src/components/Navigation/Breadcrumbs/usePageBreadcrumbs'
import useConfigurationFilter from '@app/src/hooks/configurationFilter'
import useScrollWindowToTop from '@app/src/hooks/useScrollWindowToTop'
import type { ConfigItems } from '@app/src/pages/Configurations/ConfigurationsScene'
import SustainabilityLibraryContent from '@app/src/pages/Configurations/SustainabilityLibraryContent'
import {
  AssessmentOutlined,
  DescriptionOutlined,
  HelpOutlineOutlined,
  LabelImportantOutlined,
} from '@mui/icons-material'
import { Box } from '@mui/material'
import React from 'react'
import { Helmet } from 'react-helmet'
import { useIntl } from 'react-intl'
import { generatePath, Redirect, Route, Switch, useParams, useRouteMatch } from 'react-router-dom'
import ConfigurationHubLanding from './ConfigurationHubLanding'
import { useAccount } from '@app/src/context/AccountContext'
import { insertIf } from '@app/src/utils/helpersTs'
import { WF_ORGANIZATION_ID } from '@app/src/wf-constants'

export interface SustainabilityLibraryParams {
  configurationsPage: SUSTAINABILITY_LIBRARY_PAGE_IDS
  configurationsSubPage: string
  activeTabParam: string
  activeTabSubId: string
}

export enum SUSTAINABILITY_LIBRARY_PAGE_IDS {
  Questionnaires = 'questionnaires',
  Questions = 'questions',
  Assessments = 'assessments',
  SustainabilityLibraryTags = 'sustainability-library-tags',
}

const SustainabilityLibraryScene: React.FC = () => {
  useScrollWindowToTop()
  const { formatMessage } = useIntl()
  const { path } = useRouteMatch()
  const { configurationsPage, configurationsSubPage, activeTabParam, activeTabSubId } =
    useParams<SustainabilityLibraryParams>()
  const { account } = useAccount()
  const currentOrganizationId = Number(account?.organization?.id)
  const isWfOrganisation = currentOrganizationId === WF_ORGANIZATION_ID

  const SUSTAINABILITY_LIBRARY_ITEMS: ConfigItems = [
    {
      configId: SUSTAINABILITY_LIBRARY_PAGE_IDS.Questionnaires,
      icon: DescriptionOutlined,
    },
    {
      configId: SUSTAINABILITY_LIBRARY_PAGE_IDS.Questions,
      icon: HelpOutlineOutlined,
    },
    ...insertIf(isWfOrganisation, {
      configId: SUSTAINABILITY_LIBRARY_PAGE_IDS.SustainabilityLibraryTags,
      icon: LabelImportantOutlined,
    }),
    ...insertIf(isWfOrganisation, {
      configId: SUSTAINABILITY_LIBRARY_PAGE_IDS.Assessments,
      icon: AssessmentOutlined,
      featureToggleName: 'assessments',
      permissions: [Permissions.WF_EMAIL_ACCESS],
    }),
  ]

  const filteredConfigItems = useConfigurationFilter(SUSTAINABILITY_LIBRARY_ITEMS)
  const allowedConfigIds = new Set(filteredConfigItems.map(item => item.configId))

  // When a sub-page is active, `SustainabilityLibraryCollection` owns the
  // breadcrumbs; only set them here on the hub landing so we don't overwrite
  // the child's.
  const sustainabilityLibraryCrumb = { label: formatMessage({ id: 'configurations.sustainabilityLibrary' }) }
  usePageBreadcrumbs(configurationsPage ? null : [sustainabilityLibraryCrumb])

  if (configurationsPage && !allowedConfigIds.has(configurationsPage)) {
    return <Redirect to={generatePath(path, {})} />
  }

  if (!configurationsPage) {
    return (
      <ConfigurationHubLanding
        hubTitleMessageId="configurations.sustainabilityLibrary"
        configItems={filteredConfigItems}
        routePath={path}
      />
    )
  }

  return (
    <>
      <Helmet>
        <title>{formatMessage({ id: 'configurations.sustainabilityLibrary' })}</title>
      </Helmet>

      <Box pt={2} flexGrow={1} display="flex" flexDirection="column" minWidth={0}>
        <Switch>
          <Route
            path={decodeURIComponent(
              generatePath(path, {
                configurationsPage: ':configurationsPage?',
                configurationsSubPage: ':configurationsSubPage?',
                activeTabParam: ':activeTabParam?',
                activeTabSubId: ':activeTabSubId?',
              }),
            )}
          >
            <SustainabilityLibraryContent
              content={configurationsPage}
              configurationsSubPage={configurationsSubPage}
              activeTabParam={activeTabParam}
              activeTabSubId={activeTabSubId}
            />
          </Route>
        </Switch>
      </Box>
    </>
  )
}

export default SustainabilityLibraryScene
